import React, { FunctionComponent, useEffect, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import cuid from 'cuid';
import { History, Location } from 'history';
import { parse } from 'query-string';
import useConfig from '@empiriecom/module-inspire-components/components/hooks/useConfig';
import { FontSizes } from '@empiriecom/module-inspire-components/utils/style.drupal';
import { Languages, FragmentConfig } from '@empiriecom/module-inspire-components/interfaces/types';
import { DataGlobal } from '@empiriecom/module-inspire-components/interfaces/cmsdata';
import userPreferredLocale from '@empiriecom/module-inspire-components/utils/userPreferredLocale';
import { Esi } from '@empiriecom/module-components/Esi';
import { getCmsApiUrl, getPath } from '@empiriecom/module-inspire-components/utils/apiUrl';
import { getStaticPageApiUrl } from '@empiriecom/module-inspire-components/utils/apiUrl.staticPage';
import { trackingProvider } from '@empiriecom/module-inspire-components/providers/trackingProvider';
import ComponentRecognizer from '@empiriecom/module-inspire-components/components/ComponentRecognizer';
import HistoryContext from '@empiriecom/module-inspire-components/providers/historyProvider';
import CustomNavigation from '@empiriecom/module-inspire-components/components/CustomNavigation';
import NewsletterSheet from '@empiriecom/module-inspire-components/components/Forms/NewsletterSheet';
import { CompanyId } from '@empiriecom/module-components/utils/interfaces';
import { useMedia } from 'react-media-match';
import * as request from '@empiriecom/module-inspire-components/utils/useFetch';

const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; /* horizontal alignment */
  align-items: center; /* vertical alignment */
  text-align: left;
  ${FontSizes()}
`;

const GlobalStyle = createGlobalStyle<{ desktopMinWidth?: string }>`
  .page-container {
    padding: 1rem 0 0 0;
    
    .distance-variation-1 {
      padding: 0 1.5rem;
    }
  
    .distance-fullwidth {
      max-width: unset;
    }
  }
  
  @media only screen and (min-width: ${({ desktopMinWidth }) => desktopMinWidth}) {
    .page-container {
      .distance-variation-1,
      .distance-fullwidth {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  }
  
  .promotion-accordion-overlay {
    z-index: 0 !important;
  }
`;

const StyledLegacyMiniBanner = styled.div`
  width: 100%;
  max-width: 71.25rem;
  margin: 0 auto;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
`;

const StyledPageIncludingNavigation = css`
  flex-direction: row;
  .cell-1 {
    width: 20%;
    padding-left: 0;
    padding-right: 2rem;
    padding-top: 0;
  }
  .cell-2 {
    width: 78%;
  }
`;

const StyledElementPageIncludingNavigation = styled.div`
  width: 100%;
  max-width: 71.25rem;
  display: flex;
  justify-content: space-between;
  height: 100%;
  padding-top: 2rem;
  flex-direction: column-reverse;

  .cell-1 {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 2rem;
  }
`;

const StyledElementPageIncludingNavigationTablet = styled.div`
  width: 100%;
  max-width: 71.25rem;
  display: flex;
  justify-content: space-between;
  height: 100%;
  padding-top: 2rem;
  flex-direction: column-reverse;

  .cell-1 {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 2rem;
  }

  ${StyledPageIncludingNavigation}
`;

const getLocationFromHistory = (history?: History): Location | undefined => {
  if (history) {
    return history?.location;
  }
  return undefined;
};

const PageContainer: FunctionComponent<{
  history?: History;
  testData?: any;
  configuration?: { useFallback?: boolean; fetchTimeout?: number };
  disableLegacy?: boolean;
}> = ({ history, testData, configuration, disableLegacy = false }): JSX.Element => {
  const config = useConfig<FragmentConfig>();
  const location = getLocationFromHistory(history);

  const { revisionId = '', skipEsi = false, showPromotionBanner = false } = location
    ? (parse(history ? history.location.search : '') as { [key: string]: string })
    : { revisionId: '' };

  const pathname = getPath(location, config?.apiParams?.values?.apiDefaultParams?.localesToSwitch);

  const preferredLocale = userPreferredLocale(
    location?.pathname || '',
    config?.apiParams?.values?.apiDefaultParams?.locale,
    config?.apiParams?.values?.apiDefaultParams?.localesToSwitch,
  );

  const language = preferredLocale.substring(0, 2) as Languages;

  const apiCmsUrl = getCmsApiUrl(
    language,
    pathname,
    revisionId,
    config?.apiCmsUrl?.values?.url,
    config?.replace?.values?.page,
  );

  const apiCmsUrlPageNotFound = getCmsApiUrl(
    language,
    '/error404',
    '',
    config?.apiCmsUrl?.values?.url,
    config?.replace?.values?.page,
  );

  const apiCmsUrlStaticFileCms =
    getStaticPageApiUrl({
      language,
      pathname,
      url: config?.apiCmsUrlStaticPages?.values?.url || '',
    }) || '';

  const defaultData = testData || { field_pagegroup: 'missing' };
  const fetchTimerPrimary = config?.fetchParams?.values?.timeout || 5000;
  const fetchTimerSecondary =
    configuration?.fetchTimeout || config?.fetchParams?.values?.timeoutFallback || 5000;

  const [cmsData, setCmsData] = useState<DataGlobal>(defaultData);

  useEffect(() => {
    request
      .useFetch({
        urlPrimary: apiCmsUrlStaticFileCms,
        urlSecondary: apiCmsUrl,
        urlPageNotFound: apiCmsUrlPageNotFound,
        fetchTimerPrimary,
        fetchTimerSecondary,
      })
      .then((data: any) => {
        setCmsData(data);
      });
  }, []);

  trackingProvider.page = cmsData ? cmsData.field_pagegroup || '' : 'error_404';

  trackingProvider.setLandmark({
    landmarkName: 'emma',
    template: cmsData?.field_page_landmark ? cmsData.field_page_landmark : undefined,
  });

  const itemID = cuid();

  // JPWP-12259: client side rendering of esi to prevent layout bugs in the header
  const urlESI = config?.esiUrls?.values?.minibanner?.replace('<locale>', language) || null;

  const isLandscape = useMedia({ mobile: false, tablet: true, desktop: true });

  const ElementPageContent = (): JSX.Element =>
    cmsData && cmsData?.paragraph ? (
      <>
        {!disableLegacy && !showPromotionBanner && !skipEsi && urlESI && (
          <StyledLegacyMiniBanner>
            <Esi src={urlESI} />
          </StyledLegacyMiniBanner>
        )}
        <ComponentRecognizer content={cmsData.paragraph} key={itemID} />
        <div data-prerender="true" data-testid="cms-prerender-flag" />
      </>
    ) : (
      <></>
    );

  const ElementPageIncludingNavigation = (): JSX.Element =>
    isLandscape ? (
      <StyledElementPageIncludingNavigationTablet>
        <div className="cell cell-1">
          <CustomNavigation contentData={cmsData} />
        </div>
        <div className="cell cell-2">
          <ElementPageContent />
        </div>
      </StyledElementPageIncludingNavigationTablet>
    ) : (
      <StyledElementPageIncludingNavigation>
        <div className="cell cell-1">
          <CustomNavigation contentData={cmsData} />
        </div>
        <div className="cell cell-2">
          <ElementPageContent />
        </div>
      </StyledElementPageIncludingNavigation>
    );

  return (
    <>
      <GlobalStyle desktopMinWidth={config?.mediaBreakpoints?.values?.desktopMinWidth || ''} />
      <StyledPageContainer className="page-container" data-testid="page-container">
        <HistoryContext.Provider value={{ history }}>
          <NewsletterSheet companyid={config?.global?.values?.FRAGMENT_COMPANYID as CompanyId} />
          {cmsData &&
            (cmsData?.menus?.active && !cmsData?.menus?.shop?.active ? (
              <ElementPageIncludingNavigation />
            ) : (
              <ElementPageContent />
            ))}
        </HistoryContext.Provider>
      </StyledPageContainer>
    </>
  );
};

export default PageContainer;
